import React, { useEffect, useState, useMemo } from "react";
import cogoToast from "cogo-toast";
import { genericPost } from "../../../services/Api";
import { SUCCESS_RESULT, ENDPOINTS } from "../../../Constants";
import { Loader } from "react-overlay-loader";
import "react-overlay-loader/styles.css";
import { Spinner } from "reactstrap";
import * as XLSX from "xlsx";
import { useHistory } from "react-router-dom";

const DailyAttendanceReport = () => {
  const today = useMemo(() => {
    const d = new Date();
    const month = `${d.getMonth() + 1}`.padStart(2, "0");
    const day = `${d.getDate()}`.padStart(2, "0");
    return `${d.getFullYear()}-${month}-${day}`;
  }, []);

  const history = useHistory();
  
  const [selectedDate, setSelectedDate] = useState(today);
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");

  const handleExportToExcel = () => {
    if (!rows || rows.length === 0) {
      cogoToast.error("No data available to export", { hideAfter: 3 });
      return;
    }

    try {
      const exportData = filteredRows.map((row, index) => ({
        "#": index + 1,
        "Staff ID": row.staff_id || "",
        "Staff Name": row.staff_name || "",
        Email: row.email || "",
        Office: row.office || "",
        Designation: row.designation || "",
        Date: row.date || selectedDate || "",
        "Time In": row.time_in || "",
        "Time Out": row.time_out || "",
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Daily Attendance");

      const fileName = `Daily_Attendance_${selectedDate || "Report"}_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (e) {
      console.error("Excel export error", e);
      cogoToast.error("Failed to export to Excel.", { hideAfter: 5 });
    }
  };

  const loadReport = (dateOverride) => {
    const dateToUse = dateOverride || selectedDate;
    if (!dateToUse) {
      cogoToast.error("Please select a date.", {});
      return;
    }
    setIsLoading(true);
    genericPost(ENDPOINTS.DAILY_ATTENDANCE_REPORT, {
      date: dateToUse,
  })
      .then((res) => {
        setIsLoading(false);
        if (res.status === 200) {
          if (res.data.status === SUCCESS_RESULT) {
            setRows(res.data.data || []);
          } else {
            cogoToast.error(res.data.message || "Failed to load report.", {});
          }
        } else {
          cogoToast.error("Failed to load report.", {});
        }
      })
      .catch((err) => {
        setIsLoading(false);
        console.log(err);
        cogoToast.error("Something went wrong while loading the report.", {});
      });
  };

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredRows = useMemo(() => {
    if (!search) return rows;
    const term = search.toLowerCase();
    return rows.filter((row) => {
      return (
        (row.staff_id && row.staff_id.toLowerCase().includes(term)) ||
        (row.staff_name && row.staff_name.toLowerCase().includes(term)) ||
        (row.email && row.email.toLowerCase().includes(term)) ||
        (row.office && row.office.toLowerCase().includes(term)) ||
        (row.designation && row.designation.toLowerCase().includes(term))
      );
    });
  }, [rows, search]);

  return (
    <div id="daily-attendance-print-area" className="container-fluid">
      <Loader loading={isLoading} />
      <div className="row align-items-center mb-2 mt-2">
        <div className="col-md-6">
          <h2 style={{ fontSize: "1.4rem", fontWeight: 600 }}>
            Daily Attendance Report
          </h2>
          <div style={{ fontSize: "0.9rem", color: "#6c757d" }}>
            View staff clock-in and clock-out times for a specific day.
        </div> 
  </div>
        <div className="col-md-6 text-md-end mt-2 mt-md-0">
          <button
            className="btn btn-outline-success btn-sm me-2"
            onClick={handleExportToExcel}
            disabled={isLoading || filteredRows.length === 0}
          >
            Export to Excel
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={loadReport}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Spinner size="sm" /> Loading...
              </>
            ) : (
              "Refresh"
            )}
          </button>
        </div>
      </div>

      <div
        className="card mb-3"
        style={{ boxShadow: "0 2px 4px rgba(0,0,0,0.05)" }}
      >
        <div className="card-body py-2">
          <div className="row g-2 align-items-end">
            <div className="col-md-3 col-sm-6">
              <label
                htmlFor="date"
                className="form-label"
                style={{ fontSize: "0.85rem", marginBottom: "0.25rem" }}
              >
                Date
              </label>
              <input
                id="date"
                type="date"
                className="form-control form-control-sm"
                value={selectedDate}
                onChange={(e) => {
                  const newDate = e.target.value;
                  setSelectedDate(newDate);
                  loadReport(newDate);
                }}
              />
            </div>
            <div className="col-md-4 col-sm-6">
              <label
                htmlFor="search"
                className="form-label"
                style={{ fontSize: "0.85rem", marginBottom: "0.25rem" }}
              >
                Search (name, email, office, designation)
              </label>
              <input
                id="search"
                type="text"
                className="form-control form-control-sm"
                placeholder="Type to filter..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="col-md-2 col-sm-6">
              <label
                className="form-label"
                style={{ fontSize: "0.85rem", marginBottom: "0.25rem" }}
              >
                Total Records
              </label>
              <div style={{ fontSize: "1rem", fontWeight: 600 }}>
                {filteredRows.length}
              </div>
            </div>
 </div>
        </div>
      </div>
     
      <div
        className="card"
        style={{
          boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
          borderRadius: "0.5rem",
        }}
      >
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-striped table-hover table-sm mb-0">
              <thead
                className="table-light"
                style={{ position: "sticky", top: 0, zIndex: 1 }}
              >
                <tr>
                  <th style={{ width: "50px" }}>#</th>
                  <th>Staff ID</th>
                  <th>Staff Name</th>
                  <th>Email</th>
                  <th>Office</th>
                  <th>Designation</th>
                  <th>Date</th>
                  <th>Time In</th>
                  <th>Time Out</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 && !isLoading && (
                  <tr>
                    <td colSpan={9} className="text-center py-3">
                      No records found for the selected date.
                    </td>
                  </tr>
                )}
                {filteredRows.map((row, index) => {
                  const dateStr = row.date || selectedDate;
                  let month = null;
                  let year = null;
                  if (dateStr) {
                    const d = new Date(dateStr);
                    if (!isNaN(d)) {
                      month = d.getMonth() + 1;
                      year = d.getFullYear();
                    }
                  }

                  return (
                    <tr
                      key={row.id || index}
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        if (row && row.staff_id) {
                          history.push({
                            pathname: "/hrm/attendance/attendance-details",
                            state: {
                              data: {
                                staff: row,
                                date: dateStr,
                                month: month,
                                year: year,
                              },
                            },
                          });
                        }
                      }}
                    >
                    <td>{index + 1}</td>
                    <td>{row.staff_id}</td>
                    <td>{row.staff_name}</td>
                    <td>{row.email}</td>
                    <td>{row.office}</td>
                    <td>{row.designation}</td>
                    <td>{row.date}</td>
                    <td>{row.time_in}</td>
                    <td>{row.time_out}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyAttendanceReport;
 
